import { api } from '@/lib/api'

/**
 * EP-161 §2.3 (T161-R03b): the photo of the cash a cash settlement needs.
 *
 * The PUT is presigned on the parking session (a payment may not exist yet,
 * the overstay case), valid for 180 seconds, and only the latest presign
 * counts: the server keeps its key and time on the session and the
 * settlement checks the photo against them. So the photo is uploaded the
 * moment it is taken, and taking another one replaces it.
 *
 * Answers the storage key to send as `cash_photo_key`. A refused presign
 * rejects with the axios error (its `error.code`, e.g. `already_paid`); a
 * failed PUT rejects with a plain Error.
 */
export async function uploadCashPhoto(sessionId: number, file: File): Promise<string> {
  const contentType = file.type || 'image/jpeg'
  const { data } = await api.post<{ data: { url: string; key: string } }>(
    `/api/external_parking_sessions/${sessionId}/presigned_url`,
    { purpose: 'cash_photo', content_type: contentType },
  )
  // The presign carries the content type, so the PUT must send the same one.
  const response = await fetch(data.data.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
  if (!response.ok) throw new Error(`cash photo upload failed with status ${response.status}`)
  return data.data.key
}

// The settlement's refusals with their own copy under sessions.gate.errors;
// anything else reads as "failed".
const SETTLE_ERRORS: readonly string[] = [
  'cash_photo_required',
  'cash_photo_invalid',
  'cash_photo_stale',
  'already_settled',
  'invalid_amount',
  'amount_required',
]
// A photo the server refused: it is dropped and taken again.
const PHOTO_REFUSALS: readonly string[] = ['cash_photo_required', 'cash_photo_invalid', 'cash_photo_stale']

function errorCode(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: { code?: string } } } }).response?.data?.error?.code
}

/** The copy key under sessions.gate.errors for a failed settlement. */
export function settleErrorCode(e: unknown): string {
  const code = errorCode(e)
  return code && SETTLE_ERRORS.includes(code) ? code : 'failed'
}

export function isPhotoRefusal(code: string): boolean {
  return PHOTO_REFUSALS.includes(code)
}

/**
 * The copy key under sessions.gate.errors for the photo step: a refusal that
 * dropped the photo, a presign refused because the car has paid, or a failed
 * upload for anything else.
 */
export function cashPhotoErrorShown(code: string | null): string | null {
  if (!code) return null
  return isPhotoRefusal(code) || code === 'already_paid' ? code : 'upload_failed'
}
